import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Mail,
  ShieldAlert,
  ShieldCheck,
  Send,
  RefreshCw,
  ExternalLink,
  Copy,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DNS_RECORDS = [
  {
    type: 'MX',
    host: 'send.propveiw.com',
    value: 'feedback-smtp.us-east-1.amazonses.com',
    priority: '10',
    note: 'Return-Path routing for bounces (values shown by Resend after you add the domain).',
  },
  {
    type: 'TXT',
    host: 'send.propveiw.com',
    value: 'v=spf1 include:amazonses.com ~all',
    note: 'SPF: authorises Resend to send on your behalf.',
  },
  {
    type: 'TXT',
    host: 'resend._domainkey.propveiw.com',
    value: 'p=MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQC… (unique per domain, copy from Resend dashboard)',
    note: 'DKIM: cryptographic signature proving the email came from you.',
  },
  {
    type: 'TXT',
    host: '_dmarc.propveiw.com',
    value: 'v=DMARC1; p=none;',
    note: 'DMARC (optional but recommended). Start with p=none to monitor, tighten later.',
  },
];

export default function EmailHealthView() {
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState({ events: [], counts: { sent: 0, test_mode_blocked: 0, error: 0, delivered: 0, bounced: 0, complained: 0 } });
  const [logLoading, setLogLoading] = useState(false);
  const [kindFilter, setKindFilter] = useState('');

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/email/health`, { withCredentials: true });
      setHealth(res.data);
      if (!testTo) setTestTo(res.data.sender_email?.startsWith('onboarding@') ? '' : '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load email health');
    } finally {
      setLoading(false);
    }
  }, [testTo]);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = { limit: 50 };
      if (kindFilter) params.kind = kindFilter;
      const res = await axios.get(`${API}/api/admin/email/log`, {
        params,
        withCredentials: true,
      });
      setLog(res.data);
    } catch (err) {
      // Silently ignore if unauthorized
      if (err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || 'Failed to load delivery log');
      }
    } finally {
      setLogLoading(false);
    }
  }, [kindFilter]);

  useEffect(() => { loadHealth(); }, [loadHealth]);
  useEffect(() => { loadLog(); }, [loadLog]);

  const clearLog = async () => {
    if (!confirm('Clear the entire email delivery log?')) return;
    try {
      await axios.delete(`${API}/api/admin/email/log`, { withCredentials: true });
      toast.success('Delivery log cleared');
      loadLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear log');
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/email/test`,
        testTo ? { to: testTo } : {},
        { withCredentials: true },
      );
      toast.success(res.data.message || 'Test email sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSending(false);
      loadLog();
    }
  };

  const copy = (text, label = 'value') => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${label}`);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="email-health-view">
        <p className="text-sm text-slate-500">Loading email health…</p>
      </div>
    );
  }

  const testMode = !!health?.is_test_domain;

  return (
    <div className="space-y-6" data-testid="email-health-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="h-6 w-6" /> Email Health
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Diagnostic view for platform admins to confirm transactional email delivery.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadHealth} data-testid="refresh-email-health">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Status card */}
      <Card
        className={testMode ? 'border-amber-300 bg-amber-50/40' : 'border-emerald-300 bg-emerald-50/40'}
        data-testid="email-health-status-card"
      >
        <CardContent className="p-6 flex items-start gap-4">
          {testMode ? (
            <ShieldAlert className="h-8 w-8 text-amber-600 flex-shrink-0" />
          ) : (
            <ShieldCheck className="h-8 w-8 text-emerald-600 flex-shrink-0" />
          )}
          <div className="flex-1">
            <p className="font-semibold text-slate-900">
              {testMode ? 'Resend is in TEST mode' : 'Custom sender domain configured'}
            </p>
            <p className="text-sm text-slate-700 mt-1">{health?.recommendation}</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
              <div><span className="text-slate-500">Sender:</span> <code>{health?.sender_email}</code></div>
              <div><span className="text-slate-500">From name:</span> {health?.from_name}</div>
              <div><span className="text-slate-500">API key:</span> {health?.api_key_configured ? <code>{health.api_key_preview}</code> : <span className="text-red-600">NOT SET</span>}</div>
              <div><span className="text-slate-500">Server time (UTC):</span> {health?.server_time_utc}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Send test */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="h-5 w-5" /> Send a test email
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test-to">Recipient (defaults to your own email)</Label>
            <div className="flex gap-2">
              <Input
                id="test-to"
                type="email"
                placeholder="Leave blank to send to yourself"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                data-testid="email-health-test-to"
              />
              <Button onClick={sendTest} disabled={sending} data-testid="email-health-send-test">
                {sending ? 'Sending…' : 'Send test'}
              </Button>
            </div>
          </div>
          {testMode && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              While in test mode, Resend will refuse to deliver to any address other
              than the one your Resend account is registered under. Verify the
              domain first, then come back and send a test to any inbox.
            </p>
          )}
        </CardContent>
      </Card>

      {/* DNS setup guide */}
      {testMode && (
        <Card data-testid="email-health-dns-guide">
          <CardHeader>
            <CardTitle className="text-lg">Verify propveiw.com in Resend</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-slate-700">
            <ol className="list-decimal list-inside space-y-2">
              <li>
                Sign in to Resend and open{' '}
                <a
                  href="https://resend.com/domains"
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-900 underline inline-flex items-center gap-1"
                >
                  resend.com/domains <ExternalLink className="h-3 w-3" />
                </a>{' '}
                → <strong>Add Domain</strong> → enter <code>propveiw.com</code>.
              </li>
              <li>
                Resend will show you 3–4 DNS records. Paste them into your DNS
                provider (Cloudflare / Namecheap / GoDaddy). The values in the
                table below are illustrative — copy the exact values Resend gives
                you, since the DKIM key is unique per domain.
              </li>
              <li>
                Wait 5–15 minutes for propagation. Click <strong>Verify</strong>
                in Resend until it turns green.
              </li>
              <li>
                Set <code>SENDER_EMAIL=no-reply@propveiw.com</code> in
                <code> /app/backend/.env</code> (or the deployed secrets),
                restart the backend, and hit <strong>Refresh</strong> above.
              </li>
              <li>
                Come back here and send a test email to a real inbox to confirm.
              </li>
            </ol>

            <div className="overflow-x-auto mt-4">
              <table className="min-w-full text-xs border rounded-lg">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Host</th>
                    <th className="text-left px-3 py-2">Value</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {DNS_RECORDS.map((r, i) => (
                    <tr key={`${r.type}-${r.host}`} className="border-t">
                      <td className="px-3 py-2 font-mono">{r.type}</td>
                      <td className="px-3 py-2 font-mono break-all">{r.host}</td>
                      <td className="px-3 py-2 font-mono break-all">
                        {r.value}
                        <div className="text-slate-500 font-sans mt-1">{r.note}</div>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copy(r.value, `${r.type} value`)}
                          data-testid={`copy-dns-${r.type.toLowerCase()}-${i}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500">
              Tip: Resend recommends using a dedicated subdomain like{' '}
              <code>send.propveiw.com</code> so the DKIM/SPF you add doesn't
              interfere with your primary Google Workspace / Microsoft 365
              mail records.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delivery Log */}
      <Card data-testid="email-delivery-log-card">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg">Delivery Log</CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Every send attempt from PROP VEIW's transactional pipeline.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="hidden sm:flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 className="h-3 w-3" /> {log.counts.delivered} delivered
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-200">
                <Send className="h-3 w-3" /> {log.counts.sent} sent
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                <XCircle className="h-3 w-3" /> {log.counts.bounced} bounced
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                <AlertTriangle className="h-3 w-3" /> {log.counts.test_mode_blocked} blocked
              </span>
            </div>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              className="text-xs border rounded-md px-2 py-1 bg-white"
              data-testid="delivery-log-kind-filter"
            >
              <option value="">All types</option>
              <option value="booking_confirmation">Booking confirmation</option>
              <option value="verify_email">Verify email</option>
              <option value="password_reset">Password reset</option>
              <option value="staff_invite">Staff invite</option>
              <option value="my_day_digest">My-Day-Digest</option>
              <option value="admin_test">Admin test</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={loadLog}
              disabled={logLoading}
              data-testid="delivery-log-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearLog}
              disabled={log.events.length === 0}
              data-testid="delivery-log-clear"
              title="Clear log"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {log.events.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              No email attempts logged yet. Trigger one by sending a test above,
              inviting a staff member, or completing a password reset.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Recipient</th>
                    <th className="text-left px-3 py-2">Sender</th>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {log.events.map((e) => (
                    <StatusRow key={e.id} event={e} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const STATUS_STYLES = {
  sent: {
    icon: CheckCircle2,
    className: 'text-slate-700 bg-slate-50 border-slate-200',
    label: 'Sent',
  },
  delivered: {
    icon: CheckCircle2,
    className: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    label: 'Delivered',
  },
  bounced: {
    icon: XCircle,
    className: 'text-red-700 bg-red-50 border-red-200',
    label: 'Bounced',
  },
  complained: {
    icon: AlertTriangle,
    className: 'text-orange-700 bg-orange-50 border-orange-200',
    label: 'Complained',
  },
  delayed: {
    icon: AlertTriangle,
    className: 'text-amber-700 bg-amber-50 border-amber-200',
    label: 'Delayed',
  },
  test_mode_blocked: {
    icon: AlertTriangle,
    className: 'text-amber-700 bg-amber-50 border-amber-200',
    label: 'Blocked (test mode)',
  },
  error: {
    icon: XCircle,
    className: 'text-red-700 bg-red-50 border-red-200',
    label: 'Error',
  },
};

const KIND_LABELS = {
  booking_confirmation: 'Booking',
  verify_email: 'Verify',
  password_reset: 'Reset',
  staff_invite: 'Invite',
  my_day_digest: 'Digest',
  admin_test: 'Test',
  webhook_orphan: 'Orphan',
};

function StatusRow({ event }) {
  // Prefer the terminal delivery status (from webhook) over the initial
  // send status so the pill always reflects the latest known state.
  const effective = event.delivery_status || event.status;
  const style = STATUS_STYLES[effective] || STATUS_STYLES.error;
  const Icon = style.icon;
  const when = new Date(event.created_at).toLocaleString();
  const history = event.delivery_history || [];
  return (
    <tr className="border-t" data-testid={`delivery-log-row-${event.id}`}>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border ${style.className}`}>
          <Icon className="h-3 w-3" /> {style.label}
        </span>
      </td>
      <td className="px-3 py-2 text-slate-700">{KIND_LABELS[event.kind] || event.kind}</td>
      <td className="px-3 py-2 font-mono text-xs text-slate-900">{event.to}</td>
      <td className="px-3 py-2 font-mono text-xs text-slate-500 truncate max-w-[200px]">{event.sender}</td>
      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{when}</td>
      <td className="px-3 py-2 text-xs text-slate-600 max-w-md">
        {event.resend_id && <div className="text-slate-400 font-mono">id: {event.resend_id}</div>}
        {event.bounce_reason && (
          <div className="text-red-600 font-medium">Bounce: {event.bounce_reason}</div>
        )}
        {event.error && !event.bounce_reason && (
          <div className="text-red-600 break-words" title={event.error}>{event.error}</div>
        )}
        {history.length > 0 && (
          <div className="mt-1 text-slate-500">
            {history.map((h, i) => (
              <span key={`${h.event}-${h.at}-${i}`} className="mr-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 mr-1" />
                {h.event.replace('email.', '')}
              </span>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}
